import { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import Grid from "@mui/material/Grid";
import { CircularProgress, Tooltip as MuiTooltip } from "@mui/material";
import PaginationView from "../../../../../../components/PaginationView/PaginationView";
import SearchComponent from "../../../../../../components/SearchComponent/SearchComponent";

import { search, warning_blue_img, delete_icon } from "../../../../../../assets/images/images";
import { swap_vert } from "../../../../../../assets/images/images";
import AlertConfirmationPopup from "../../../../../../components/AlertConformationPopup/AlertConformationPopup";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const GRADE_ORDER = [
  "Pre-Kindergarten", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4",
  "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12",
];

/**
 * Reusable table: pass header names and column config (key + type), component handles rendering.
 *
 * columns: array of { header: string, key: string, type?: "text" | "profile" | "date" | "acceptReject" }
 *   - text: row[key] (default)
 *   - profile: avatar + name (uses defaultAvatar, onStudentViewProfile)
 *   - date: formatDate(row[key])
 *   - acceptReject: Accept/Reject buttons (uses onReject, onAccept)
 *
 * leadingColumn: "checkbox" → checkbox column (pass removeStudentsArray, onDeleteCheck, onDeleteAll, data for "all" count)
 * trailingColumn: "viewProfile" → last column with Remove button in header + View Profile link in cell (pass onOpenRemovePopup, onStudentViewProfile)
 */
const StudentsTable = ({
  statusConfig = {},

  columns = [],
  data = [],
  isLoading = false,
  emptyMessage = "No records found.",
  getRowKey = (row, index) => row?.student_id ?? row?.id ?? index,
  wrapperClassName = "",
  wrapperId,

  leadingColumn,
  trailingColumn,

  formatDate = (v) => v,
  defaultAvatar,
  onStudentViewProfile,

  removeStudentsArray = [],
  onDeleteCheck,
  onDeleteAll,
  onOpenRemovePopup,
  onIndividualDelete,
  isBulkDeleteMode = false,
  isDeletePopupOpen = false,
  dataLengthForSelectAll,

  onReject,
  onAccept,

  onAccessRoleChange,
  onTransferComplete,
  myRole,

  totalPages = 0,
  next = null,
  previous = null,
  currentPage = 1,
  setCurrentPage,
  activeTab,

  showSearch = false,
  searchPlaceholder = "Search Students",
  searchByNameKey = "student_name",

  overlay,

  courseId,
  teacherId,
}) => {

  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumnHeader, setSortColumnHeader] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [transferPending, setTransferPending] = useState(null);
  const [openAccessRoleKey, setOpenAccessRoleKey] = useState(null);
  const [dropdownPos, setDropdownPos] = useState(null);
  const globalState = useSelector((state) => state.globalState);


  useEffect(() => {
    if (openAccessRoleKey == null) return;
    const handleClickOutside = (e) => {
      // The mobile card list and the desktop table both render this dropdown,
      // so a single element ref can only ever point at one of the two copies —
      // a tap on the other one reads as "outside" and closes the menu before
      // the option's click fires. Match on the row key instead.
      const wrapper = e.target?.closest?.("[data-access-role-key]");
      if (wrapper?.dataset?.accessRoleKey === String(openAccessRoleKey)) return;
      setOpenAccessRoleKey(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [openAccessRoleKey]);

  const hasLeading = leadingColumn === "checkbox";
  const hasLeadingOwner = hasLeading && myRole === "owner";
  const hasTrailing = trailingColumn === "viewProfile";
  const hasDeleteColumn = activeTab === "students" && onIndividualDelete;
  // Always allocate space for the leading checkbox column when configured,
  // even if current user is not owner (keeps table alignment consistent).
  const totalCols = (hasLeading ? 1 : 0) + columns.length + (hasTrailing ? 1 : 0) + (hasDeleteColumn ? 1 : 0);
  const hasPagination = totalPages > 1 && setCurrentPage;


  const sortableHeadersByTab = {
    students: ["Student Name", "Grade", "Enrolled On"],
    manageTeachers: ["Teacher Name"],
    activityLog: ["Date", "Teacher Name"],
    studentsRequest: ["Student Name", "Grade", "Requested On"],
  };
  const showSortIcon = (header) => {
    const allowed = sortableHeadersByTab[activeTab];
    return Array.isArray(allowed) && allowed.includes(header);
  };

  useEffect(() => {
    setSortColumnHeader(null);
    setSortDirection("asc");
  }, [activeTab]);

  const handleSortClick = (header) => {
    if (!showSortIcon(header)) return;
    const isDateCol = ["Enrolled On", "Requested On", "Date"].includes(header);
    if (sortColumnHeader === header) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumnHeader(header);
      setSortDirection(isDateCol ? "desc" : "asc");
    }
  };

  const filteredData = useMemo(() => {
    if (!showSearch || !searchQuery.trim()) return data;
    const q = searchQuery.trim().toLowerCase();
    return data.filter((row) => {
      const name = String(row?.[searchByNameKey] ?? "").toLowerCase();
      const studentId = String(row?.student_email ?? "").toLowerCase();
      return name.includes(q) || studentId.includes(q);
    });
  }, [data, showSearch, searchQuery, searchByNameKey]);

  const getCellValueForSort = (row, key) => {
    if (key.includes(".")) {
      return key.split(".").reduce((obj, k) => obj?.[k], row);
    }
    return row[key];
  };

  /** Parse display date strings: "28th Jan, 2026" or "04 Mar, 2026" -> timestamp */
  const parseDisplayDateToTime = (str) => {
    if (str == null || str === "") return NaN;
    const s = String(str).replace(/\b(\d+)(st|nd|rd|th)\b/gi, "$1").trim();
    const parts = s.split(/[\s,]+/).filter(Boolean);
    if (parts.length < 3) return NaN;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthIdx = monthNames.findIndex((m) => m.toLowerCase() === parts[1].toLowerCase());
    if (monthIdx === -1) return NaN;
    const day = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);
    if (Number.isNaN(day) || Number.isNaN(year)) return NaN;
    return new Date(year, monthIdx, day).getTime();
  };

  const sortedData = useMemo(() => {
    if (!sortColumnHeader || !filteredData.length) return filteredData;
    const col = columns.find((c) => c.header === sortColumnHeader);
    if (!col) return filteredData;

    const key = col.key;
    const isDateCol = col.type === "date" || sortColumnHeader === "Date";
    const isGrade = sortColumnHeader === "Grade";
    const isName =
      sortColumnHeader === "Name" ||
      sortColumnHeader === "Student Name" ||
      sortColumnHeader === "Teacher Name";

    const sorted = [...filteredData].sort((a, b) => {
      const valA = getCellValueForSort(a, key);
      const valB = getCellValueForSort(b, key);

      if (isGrade) {
        const indexA = GRADE_ORDER.indexOf(String(valA ?? "").trim());
        const indexB = GRADE_ORDER.indexOf(String(valB ?? "").trim());
        const idxA = indexA === -1 ? GRADE_ORDER.length : indexA;
        const idxB = indexB === -1 ? GRADE_ORDER.length : indexB;
        if (sortDirection === "asc") return idxA - idxB;
        return idxB - idxA;
      }

      if (isDateCol) {
        const timeA = col.type === "date" ? (valA ? new Date(valA).getTime() : NaN) : parseDisplayDateToTime(valA);
        const timeB = col.type === "date" ? (valB ? new Date(valB).getTime() : NaN) : parseDisplayDateToTime(valB);
        const tA = Number.isNaN(timeA) ? 0 : timeA;
        const tB = Number.isNaN(timeB) ? 0 : timeB;
        if (sortDirection === "asc") return tA - tB;
        return tB - tA;
      }

      if (isName) {
        const strA = String(valA ?? "").toLowerCase();
        const strB = String(valB ?? "").toLowerCase();
        const cmp = strA.localeCompare(strB);
        return sortDirection === "asc" ? cmp : -cmp;
      }

      return 0;
    });

    return sorted;
  }, [filteredData, sortColumnHeader, sortDirection, columns]);

  const getCellValue = (row, key) => {
    if (key.includes(".")) {
      return key.split(".").reduce((obj, k) => obj?.[k], row);
    }
    return row[key];
  };

  const renderCell = (row, col, index) => {

    const key = col.key;
    const value = getCellValue(row, key);

    switch (col.type) {
      case "profile": {
        const imageKey = col.imageKey ?? "student_profile_pic";
        const nameKey = col.nameKey ?? "student_name";
        const profileImage = getCellValue(row, imageKey) ?? row?.[imageKey];
        const profileName = getCellValue(row, nameKey) ?? row?.[nameKey] ?? value;
        const studentStatus =
          getCellValue(row, "student_status") ?? row?.student_status;

        return (
          <div
            className="studentrequest-profile"
            onClick={() => onStudentViewProfile?.(row)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && onStudentViewProfile?.(row)}
          >
            <div>
              <img
                src={profileImage || defaultAvatar}
                alt=""
                style={{ margin: '0px 10px 0px 0px;' }}
              />
            </div>

            {
              activeTab === "activityLog" ?
                (
                  <div
                    style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", overflow: 'hidden', width: '80%', justifyContent: 'flex-start' }}
                  >
                    <div>
                      {row?.teacher_name}
                      {
                        row.email === globalState?.email && (
                          <span style={{ paddingLeft: '5px' }}>{'(You)'}</span>
                        )
                      }
                    </div>
                    {row?.email && (
                      <div style={{ fontSize: "0.85em", color: "#666", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'left' }}>{row.email}</div>
                    )}
                  </div>
                ) : (
                  <div className="studentrequest-profile-name">
                    {profileName}
                  </div>
                )
            }
          </div>
        );
      }
      case "status": {
        const style = statusConfig[value] ?? { backgroundColor: "#ede9fe", color: "#a78bfa" };
        return value ? (
          <span style={{
            padding: "6px 18px",
            borderRadius: "999px",
            fontSize: "0.82em",
            fontWeight: 500,
            display: "inline-block",
            whiteSpace: "nowrap",
            letterSpacing: "0.3px",
            ...style,
          }}>
            {value}
          </span>
        ) : null;
      }
      case "date":
        return formatDate(value);
      case "acceptReject":
        if (myRole === "viewer") return null;
        return (
          <div className="student-request-btn-folder">
            <button type="button" className="accept-reject-btn" onClick={() => onReject?.(row?.student_id)}>Reject</button>
            <button type="button" className="accept-reject-btn" onClick={() => onAccept?.(4, row?.student_id)}>Accept</button>
          </div>
        );
      case "accessRole": {
        const roleCode = (value ?? "").toString().toLowerCase();
        const roleLabels = {
          owner: "Owner",
          editor: "Editor",
          viewer: "Viewer",
          remove_user: "Remove User",
        };
        const label = roleLabels[roleCode] ?? "Editor";
        if (roleCode === "owner") {
          return <span className="access-role-text" style={{ paddingLeft: myRole === "owner" && "6px", fontWeight: 600 }}>Owner</span>;
        }
        if (myRole !== "owner") {
          return <span className="access-role-text">{label}</span>;
        }
        const accessOptions = [
          { value: "owner", label: "Owner" },
          { value: "editor", label: "Editor" },
          { value: "viewer", label: "Viewer" },
          { value: "remove_user", label: "Remove User" },
        ];
        const selectValue = ["owner", "editor", "viewer", "remove_user"].includes(roleCode) ? roleCode : "editor";
        const currentLabel = roleLabels[selectValue] ?? "Editor";
        const rowKey = getRowKey(row, index);
        const isOpen = openAccessRoleKey === rowKey;
        const handleSelectRole = (newRole) => {
          if (newRole === "owner") {
            setTransferPending({
              row,
              teacherName: row?.name ?? row?.email ?? "this teacher",
            });
          } else {
            onAccessRoleChange?.(row, newRole);
          }
          setOpenAccessRoleKey(null);
        };
        return (
          <div
            className="access-role-dropdown-wrapper"
            data-access-role-key={rowKey}
            style={{ position: "relative", display: "inline-block", width: "100%" }}
          >
            <button
              type="button"
              className="access-role-dropdown-trigger"
              onClick={(e) => {
                if (isOpen) {
                  setOpenAccessRoleKey(null);
                } else {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setDropdownPos({
                    top: rect.bottom + window.scrollY + 4,
                    left: rect.left + window.scrollX,
                  });
                  setOpenAccessRoleKey(rowKey);
                }
              }}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: "inherit",
                fontFamily: "inherit",
                color: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "4px",
                width: "6rem",
                maxWidth: "90%",
              }}
            >
              <span style={{ fontWeight: 600 }}>{currentLabel}</span>
              <span style={{ fontSize: "10px", opacity: 0.7, paddingTop: "3px" }}>▼</span>
            </button>
            {isOpen && dropdownPos && createPortal(
              <div
                className="access-role-dropdown-menu"
                data-access-role-key={rowKey}
                style={{
                  position: "absolute",
                  top: dropdownPos.top,
                  left: dropdownPos.left,
                  minWidth: "140px",
                  fontSize: "13px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  padding: "6px 0",
                  zIndex: 1000,
                }}
              >
                {accessOptions.map((opt, i) => (
                  <Fragment key={opt.value}>
                    {i > 0 && (
                      <div
                        style={{
                          height: "1px",
                          backgroundColor: "#e0e0e0",
                          margin: "0 12px",
                        }}
                      />
                    )}
                    <button
                      type="button"
                      className="access-role-dropdown-option"
                      onClick={() => { handleSelectRole(opt.value) }}
                      style={{
                        display: "block",
                        width: "100%",
                        border: "none",
                        background: "none",
                        padding: "10px 16px",
                        fontSize: "inherit",
                        fontFamily: "inherit",
                        color: "#232323",
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {opt.label}
                    </button>
                  </Fragment>
                ))}
              </div>,
              document.body
            )}
          </div>
        );
      }
      default:
        if (activeTab === "activityLog" && key === "date") {
          return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px", ...(activeTab === "activityLog" ? { marginLeft: '15px' } : {}) }}>
              <span>{value ?? ""}</span>
              {row?.time != null && (
                // <span style={{ fontSize: "0.85em", color: "#666" }}>{row.time}</span>
                <span style={{ fontSize: "0.85em", color: "#666" }}>{dayjs(row.created_at).format('h:mm A')} </span>
              )}
            </div>
          );
        }
        if (activeTab === "activityLog" && key === "action") {
          return (
            <span className="activity-log-action-pill">
              {value ?? ""}
            </span>
          );
        }
        if (activeTab === "activityLog" && key === "details") {
          const invitedStudents = row?.invited_students;
          const copy_lesson = row?.copy_lesson;
          const hasInvitedStudents = Array.isArray(invitedStudents) && invitedStudents.length > 0;
          const namesList = hasInvitedStudents
            ? invitedStudents.map((s) => s?.name).filter(Boolean).join(", ")
            : "";
          const hasRemovedInvitedStudent = hasInvitedStudents
            ? invitedStudents.some((s) => s?.is_removed === true)
            : false;
          const tooltipTitle = hasRemovedInvitedStudent
            ? `${namesList} ( Removed )`
            : namesList;
          const studentIdsArray = hasInvitedStudents
            ? invitedStudents.map((s) => s?.student_id).filter((id) => id != null && id !== "")
            : [];
          const handleDetailsClick = () => {
            if (courseId && studentIdsArray.length > 0 && !copy_lesson) {
              navigate(`/invited-students/${courseId}`, {
                state: { teacher_id: teacherId, student_ids: studentIdsArray },
              });
            }
          };
          return (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                ...(hasInvitedStudents && courseId && !copy_lesson ? { cursor: "pointer" } : {}),
              }}
              {...(hasInvitedStudents && courseId && !copy_lesson
                ? {
                  onClick: handleDetailsClick,
                  onKeyDown: (e) => e.key === "Enter" && handleDetailsClick(),
                  role: "button",
                  tabIndex: 0,
                }
                : {})}
            >
              {`${value}` ?? ""}
              {hasInvitedStudents && (
                <MuiTooltip title={tooltipTitle} arrow placement="top">
                  <i
                    className="fa-solid fa-circle-info"
                    style={{ color: "#666", fontSize: "0.95em" }}
                    aria-hidden
                  />
                </MuiTooltip>
              )}
            </span>
          );
        }
        return value ?? "";
    }
  };
  const hasPurchasedStudent = sortedData.some(
    (student) => student?.student_status === "Purchased"
  );
  return (
    <div
      className={`list-of-students-wrappper ${wrapperClassName}${activeTab === "activityLog" ? " activity-log-table" : ""}`.trim()}
      style={{ boxShadow: '0 1px 8px #e3e3e3' }}
      id={wrapperId}
    >
      {showSearch && !isLoading && (
        <>
          <SearchComponent
            search={search}
            searchPlaceholder={searchPlaceholder}
            searchValue="text"
            onchange={(e) => setSearchQuery(e.target.value)}
            name="searchOption"
            value={searchQuery}
            purpose="Search Enrolled Students"
            containerStyle={{ width: "50%" }}
            inputStyle={{ backgroundColor: "white", boxShadow: "0px 1px 9px #dbdbdb" }}
          />
          <div style={{ marginBottom: "25px" }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isBulkDeleteMode && removeStudentsArray.length > 0 && !isDeletePopupOpen ? "15px" : "0px" }}>
              {isBulkDeleteMode && removeStudentsArray.length > 0 && !isDeletePopupOpen && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '13px' }}>{removeStudentsArray.length} Selected</span>
                  <button
                    type="button"
                    style={{ backgroundColor: 'transparent', color: 'red', cursor: 'pointer', border: 'none', fontSize: '13px' }}
                    onClick={() => onOpenRemovePopup?.()}
                    disabled={removeStudentsArray.length === 0}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
        </>)}

      {!isLoading && (
        <>
          <div className="mobile-cards-wrapper">
            {sortedData.length > 0 ? sortedData.map((row, index) => (
              <div
                key={`mobile-${getRowKey(row, index)}`}
                className="mobile-student-card"
                style={{ boxShadow: '0px 0px 8px 0px #0000001F' }}
              >
                {/* Header: checkbox + profile + delete (common) */}
                {activeTab !== "activityLog" && (
                  <div className="flex items-center justify-between gap-2">
                    {hasLeadingOwner && (
                      <input type="checkbox" value={row?.student_email}
                        onChange={onDeleteCheck}
                        checked={removeStudentsArray.some((el) => el === row?.student_email)}
                      />
                    )}
                    {columns.find(c => c.type === "profile") && (() => {
                      const col = columns.find(c => c.type === "profile");
                      const imageKey = col.imageKey ?? "student_profile_pic";
                      const nameKey = col.nameKey ?? "student_name";
                      const profileImage = getCellValue(row, imageKey) ?? row?.[imageKey];
                      const profileName = getCellValue(row, nameKey) ?? row?.[nameKey];
                      return (
                        <div className="studentrequest-profile flex-1 justify-start">
                          <div className="studentrequest-profile-image w-8">
                            <img className="rounded-full" src={profileImage || defaultAvatar} alt="" />
                          </div>
                          <div className="studentrequest-profile-name">{profileName}</div>
                        </div>
                      );
                    })()}
                    {hasDeleteColumn && myRole === "owner" && (
                      <img src={delete_icon} alt="Delete" role="button"
                        onClick={() => onIndividualDelete?.(row?.student_email)}
                        className="cursor-pointer h-5 w-5"
                      />
                    )}
                  </div>
                )}

                {/* Students Tab */}
                {activeTab === "students" && (
                  <>
                    <div className="flex gap-2 text-left">
                      {columns.filter(c => c.type !== "profile" && c.header !== "Email Id")
                        .map((col, colIndex) => (
                          <div key={col.key} style={{ flex: colIndex === 2 ? "none" : 1 }}>
                            <p className="text-xs text-[#000] font-medium  m-0"> {col.header === "Student Id" ? "ID" : col.header}</p>
                            <p className="text-[13px] text-[#000] font-light m-0">
                              {renderCell(row, col, index)}
                            </p>
                          </div>
                        ))}
                    </div>
                    {columns.find(c => c.header === "Email Id") && (
                      <div style={{ textAlign: 'start' }}>
                        <p className="text-xs text-[#000] font-medium m-0">Email Id</p>
                        <p className="text-[13px] font-light text-[#000] m-0">
                          {renderCell(row, columns.find(c => c.header === "Email Id"), index)}
                        </p>
                      </div>
                    )}
                    {hasTrailing && (
                      <button type="button"
                        className="flex justify-center w-full rounded-lg p-2 text-sm text-[#0068ff] bg-transparent cursor-pointer"
                        style={{
                          background: 'white',
                          border: '2px solid #0068ff',
                          color: '#0068ff'
                        }}
                        onClick={() => onStudentViewProfile?.(row)}
                      >
                        View Profile
                      </button>
                    )}
                  </>
                )}

                {/* Student Requests Tab */}
                {activeTab === "studentsRequest" && (
                  <>
                    <div className="flex gap-2 text-left">
                      {columns.filter(c => c.type !== "profile" && c.type !== "acceptReject")
                        .map((col, colIndex) => (
                          <div key={col.key} style={{ flex: colIndex === 2 ? 2 : 1 }}>
                            <p className="text-xs text-[#000] font-medium m-0"> {col.header === "Student Id" ? "ID" : col.header}</p>
                            <p className="text-[13px] font-light text-[#000] m-0">
                              {renderCell(row, col, index)}
                            </p>
                          </div>
                        ))}
                    </div>
                    {columns.find(c => c.type === "acceptReject") && myRole !== "viewer" && (
                      <div>{renderCell(row, columns.find(c => c.type === "acceptReject"), index)}</div>
                    )}
                  </>
                )}

                {/* Manage Teachers Tab */}
                {activeTab === "manageTeachers" && (
                  <div className="flex flex-col gap-2 text-left">
                    {/* ID */}
                    <div style={{ flex: 1 }}>
                      <p className="text-xs text-[#000] font-medium m-0">Email ID</p>
                      <p className="text-[13px] font-light text-[#000] m-0 md:pl-[6px]">
                        {row?.email ?? "-"}
                      </p>
                    </div>
                    {/* Access */}
                    {columns.find(c => c.type === "accessRole") && (
                      <div style={{ flex: 1 }}>
                        <p className="text-xs text-[#000] font-medium m-0">Access</p>
                        <div className="text-[13px] font-light text-[#000]">
                          {renderCell(row, columns.find(c => c.type === "accessRole"), index)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Activity Log Tab */}
                {activeTab === "activityLog" && (
                  <>
                    {/* Profile + Action pill header - remove the wrong condition */}
                    <div className="flex items-center justify-between gap-2">
                      {columns.find(c => c.type === "profile") && (() => {
                        const col = columns.find(c => c.type === "profile");
                        const imageKey = col.imageKey ?? "ai_avatar";
                        const nameKey = col.nameKey ?? "teacher_name";
                        const profileImage = getCellValue(row, imageKey) ?? row?.[imageKey];
                        const profileName = getCellValue(row, nameKey) ?? row?.[nameKey];
                        return (
                          <div className="studentrequest-profile flex-1 justify-start">
                            <div className="studentrequest-profile-image">
                              <img className="rounded-full" src={profileImage || defaultAvatar} alt="" />
                            </div>
                            <div className="studentrequest-profile-name">{profileName}</div>
                          </div>
                        );
                      })()}
                      {columns.find(c => c.key === "action") && (
                        <span className="activity-log-action-pill">
                          {getCellValue(row, "action") ?? ""}
                        </span>
                      )}
                    </div>

                    {/* Date */}
                    {columns.find(c => c.key === "date") && (
                      <div style={{ textAlign: 'start' }}>
                        <p className="text-xs text-[#000] font-medium m-0">Date</p>
                        <span className="text-[13px] font-light text-[#000] m-0">
                          {getCellValue(row, "date") ?? ""}
                        </span>
                        {row?.created_at && (
                          <span className="text-xs text-[#000] m-0">
                            , {dayjs(row.created_at).format('h:mm A')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Details */}
                    {columns.find(c => c.key === "details") && (
                      <div style={{ textAlign: 'start' }}>
                        <p className="text-xs text-[#000] font-medium m-0">Details</p>
                        <div className="text-[13px] font-light text-[#000]">
                          {renderCell(row, columns.find(c => c.key === "details"), index)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )) : null}
          </div>
          <table className="desktop-table" style={{ width: activeTab === "manageTeachers" && "70%" }}>
            <thead>
              <tr style={{ boxShadow: 'none', backgroundColor: 'transparent ' }}>
                {/* {hasLeading && (
                  <th data-label="" style={{ width: "40px" }}>
                    {hasLeadingOwner ? (
                      
                      <input
                        type="checkbox"
                        value="all"
                        onChange={onDeleteAll}
                        checked={removeStudentsArray.length > 0 && removeStudentsArray.length === (dataLengthForSelectAll ?? data.length)}
                        disabled={!(dataLengthForSelectAll ?? data?.length)}
                      />
                    ) : (
                      // Keep column width/alignment; hide interactive control for non-owners
                      <span aria-hidden style={{ visibility: "hidden" }}>
                        <input type="checkbox" disabled />
                      </span>
                    )}
                  </th>
                )} */}
                {hasLeading && (
                  <th data-label="" style={{ width: "40px" }}>
                    {hasLeadingOwner ? (
                      hasPurchasedStudent ? (
                        ""
                      ) : (
                        <input
                          type="checkbox"
                          value="all"
                          onChange={onDeleteAll}
                          checked={
                            removeStudentsArray.length > 0 &&
                            removeStudentsArray.length ===
                            (dataLengthForSelectAll ?? data.length)
                          }
                          disabled={!(dataLengthForSelectAll ?? data?.length)}
                        />
                      )
                    ) : (
                      <span aria-hidden style={{ visibility: "hidden" }}>
                        <input type="checkbox" disabled />
                      </span>
                    )}
                  </th>
                )}
                {columns.map((col) => {
                  const headerAlignLeft =
                    (activeTab === "manageTeachers" && (col.header === "Access" || col.header === "Email Id")) ||
                    (activeTab === "activityLog" && col.header === "Details") ||
                    (activeTab === "students" && col.header === "Email Id");
                  return (
                    <th
                      key={col.key}
                      data-label={col.header}
                      {...(headerAlignLeft ? { style: { textAlign: "left" } } : {})}
                    >
                      {col.header}
                      {showSortIcon(col.header) && (
                        <img
                          src={swap_vert}
                          alt=""
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSortClick(col.header)}
                          onKeyDown={(e) => e.key === "Enter" && handleSortClick(col.header)}
                          style={{ marginLeft: "5px", verticalAlign: "middle", height: "14px", cursor: "pointer" }}
                        />
                      )}
                    </th>
                  );
                })}
                {hasTrailing && (
                  <th data-label="">
                    View
                  </th>
                )}
                {hasDeleteColumn && (
                  <th data-label="">Action</th>
                )}
              </tr>
              {/* <div className="list-of-students-gap" /> */}
            </thead>
            <tbody >
              {sortedData.length > 0 ? (
                sortedData.map((row, index) => (
                  <Fragment key={getRowKey(row, index)}>
                    <tr className="desktop-student-row" style={{ boxShadow: 'none', backgroundColor: 'transparent ' }}>
                      {hasLeading && (
                        // <td style={{ width: "40px" }}>
                        //   {hasLeadingOwner ? (
                        //     <input
                        //       value={row?.student_email}
                        //       type="checkbox"
                        //       onChange={onDeleteCheck}
                        //       checked={removeStudentsArray.some((el) => el === row?.student_email)}
                        //       disabled={row?.student_status === "Purchased"}

                        //     />
                        //   ) : (
                        //     <span aria-hidden style={{ visibility: "hidden" }}>
                        //       <input type="checkbox" disabled />
                        //     </span>
                        //   )}
                        // </td>

                        <td style={{ width: "40px" }}>
                          {hasLeadingOwner ? (
                            row?.student_status === "Purchased" ? (
                              ""
                            ) : (
                              <input
                                value={row?.student_email}
                                type="checkbox"
                                onChange={onDeleteCheck}
                                checked={removeStudentsArray.some((el) => el === row?.student_email)}
                              />
                            )
                          ) : (
                            <span aria-hidden style={{ visibility: "hidden" }}>
                              <input type="checkbox" disabled />
                            </span>
                          )}
                        </td>
                      )}
                      {columns.map((col) => {
                        const alignLeft =
                          (activeTab === "manageTeachers" && (col.header === "Access" || col.header === "Email Id")) ||
                          (activeTab === "activityLog" && col.header === "Details") ||
                          (activeTab === "students" && col.header === "Email Id");
                        const tdStyle = {
                          ...(col.type === "acceptReject" ? { width: "30%" } : {}),
                          ...(alignLeft ? { textAlign: "left" } : {}),
                        };
                        return (
                          <td
                            key={col.key}
                            data-label={col.header}
                            style={{ paddingBottom: '10px' }}
                            {...(Object.keys(tdStyle).length > 0 ? { style: tdStyle } : {})}
                          >
                            {renderCell(row, col, index)}
                          </td>
                        );
                      })}
                      {hasTrailing && (
                        <td data-label="">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              onStudentViewProfile?.(row);
                            }}
                          >
                            View Profile
                          </a>
                        </td>
                      )}
                      {/* {(hasDeleteColumn && myRole === "owner") && (
                        <td>
                          <img
                            src={delete_icon}
                            alt="Delete"
                            role="button"
                            tabIndex={0}
                            onClick={() => onIndividualDelete?.(row?.student_email)}
                            onKeyDown={(e) => e.key === "Enter" && onIndividualDelete?.(row?.student_email)}
                            style={{ cursor: "pointer", height: "20px", width: "20px" }}
                          />
                        </td>
                      )}                 */}
                      {hasDeleteColumn && myRole === "owner" && (
                        <td>
                          {row?.student_status === "Purchased" ? (
                            "-"
                          ) : (
                            <img
                              src={delete_icon}
                              alt="Delete"
                              role="button"
                              tabIndex={0}
                              onClick={() => onIndividualDelete?.(row?.student_email)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && onIndividualDelete?.(row?.student_email)
                              }
                              style={{
                                cursor: "pointer",
                                height: "20px",
                                width: "20px",
                              }}
                            />
                          )}
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))
              ) : (
                <tr style={{ boxShadow: 'none', backgroundColor: 'transparent ' }} >
                  <td colSpan={totalCols} data-label={emptyMessage}>
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      <div style={{ alignItems: "center", justifyContent: "center" }}>
        {isLoading && (
          <Grid
            height="50vh"
            xs={12}
            container
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Grid>
        )}
      </div>

      {hasPagination && (
        <PaginationView
          totalPages={totalPages}
          next={next}
          previous={previous}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}

      {overlay}

      {transferPending && (
        <AlertConfirmationPopup
          alert_image={warning_blue_img}
          alert_title1={`Are you sure you want to transfer this course to ${transferPending.teacherName}?`}
          alert_title2="You will no longer be able to manage permissions for this course"
          btn1="Cancel"
          btn2="Transfer Course"
          onCancel={() => setTransferPending(null)}
          onAccept={() => {
            onAccessRoleChange?.(transferPending.row, "owner");
            setTransferPending(null);
            setTimeout(() => {
              onTransferComplete?.();
            }, 200);
          }}
        />
      )}
    </div>
  );
};

export default StudentsTable;